import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "semantic-ui-react";
import { useTextResources } from "../../../language";
import PropTypes from "prop-types";
import { onWarning } from "../../../components/shared/messages";
import NumericInput from "../../../components/shared/NumericInput";
import CheckBoxToggle from "../../../components/shared/CheckBoxToggle";
import AddonsConsumptionTypesDropDown from "../../../components/shared/AddonsConsumptionTypesDropDown";
import WeekDaysSelect from "../../../components/shared/WeekDaysSelect";
import { AddonConsumptionType } from "../../../components/shared/constants";
import { makeDaysOfWeekArrayIntoInt, makeIntToDaysOfWeekArray } from "../../../components/shared/utils";

export const AddonOptionsPopup = ({ open, onClose, hotelAddon, onUpdate, isDerived, rateElementName, isConsumptionAfterReservationDayDefault }) => {
  const minEveryXnightsValue = 2;
  const text = useTextResources();
  const [updatedHotelAddon, setUpdatedHotelAddon] = useState({
    ConsumptionType: AddonConsumptionType.EveryNight
  });

  useEffect(() => {
    if (hotelAddon && open) {
      setUpdatedHotelAddon({
        ...hotelAddon,
        IsConsumptionAfterReservationDay: hotelAddon?.IsConsumptionAfterReservationDay ?? isConsumptionAfterReservationDayDefault
      });
    }
    else {
      setUpdatedHotelAddon({
        ConsumptionType: AddonConsumptionType.EveryNight,
        IsConsumptionAfterReservationDay: isConsumptionAfterReservationDayDefault
      });
    }
  }, [hotelAddon, open]);

  const isValidBeforeSave = () => {
    const warningMessage = "Price and Posting is mandatory field";
    const isEveryXNights = updatedHotelAddon.ConsumptionType === AddonConsumptionType.EveryXNights;
    const isOnSpecificWeekdays = updatedHotelAddon.ConsumptionType === AddonConsumptionType.OnSpecificWeekdays;
    const isOnSpecificNightsOfTheStay = updatedHotelAddon.ConsumptionType === AddonConsumptionType.OnSpecificNightsOfTheStay;

    if (isEveryXNights) {
      const everyXnights = updatedHotelAddon?.EveryXnights;
      if (isFinite(everyXnights) && everyXnights >= minEveryXnightsValue) {
        return true;
      } else {
        onWarning(warningMessage);
        return false;
      }
    }

    if (isOnSpecificWeekdays) {
      const onSpecificWeekdays = updatedHotelAddon?.OnSpecificWeekdays;
      if (isFinite(onSpecificWeekdays) && onSpecificWeekdays > 0) {
        return true;
      } else {
        onWarning(warningMessage);
        return false;
      }
    }

    if (isOnSpecificNightsOfTheStay) {
      const onSpecificNightsOfTheStay = updatedHotelAddon?.OnSpecificNightsOfTheStay;
      if (onSpecificNightsOfTheStay?.trim()) {
        return true;
      } else {
        onWarning(warningMessage);
        return false;
      }
    }

    return true;
  };

  const onSave = () => {
    if (!isValidBeforeSave()) return;

    onUpdate(updatedHotelAddon);
  };

  const onChange = (fieldName, value) => {
    if (fieldName === "ConsumptionType") {
      setUpdatedHotelAddon((prev) => ({
        ...prev,
        EveryXnights: null,
        OnSpecificNightsOfTheStay: null,
        OnSpecificWeekdays: null,
        [fieldName]: value
      }));
    }
    else {
      setUpdatedHotelAddon((prev) => ({
        ...prev,
        [fieldName]: value
      }));
    }
  }

  return (
    <Modal size="small" open={open} onClose={onClose} dimmer centered>
      <Modal.Header>{`${text("EditAddonOptions")} - ${rateElementName}`}</Modal.Header>
      <Modal.Content>
        <Form className="addon-options-form">
          <Form.Group widths={4} className="flex-align-center">
            <Form.Field>{text("MaxPax")}</Form.Field>
            <Form.Field
              buttonPlacement="right"
              control={NumericInput}
              min={0}
              selectOnFocus
              digits={0}
              nullable
              value={updatedHotelAddon.MaxPax}
              onChange={(value) => onChange("MaxPax", value)}
              disabled={isDerived}
            />
            <Form.Field>{text("MaxNights")}</Form.Field>
            <Form.Field
              buttonPlacement="right"
              control={NumericInput}
              min={0}
              selectOnFocus
              digits={0}
              nullable
              value={updatedHotelAddon.MaxNights}
              onChange={(value) => onChange("MaxNights", value)}
              disabled={isDerived}
            />
          </Form.Group>
          <Form.Group widths={2} className="flex-align-center">
            <Form.Field>{text("PartyReservationQuantity")}</Form.Field>
            <Form.Field
              buttonPlacement="right"
              control={NumericInput}
              min={0}
              selectOnFocus
              digits={0}
              nullable
              value={updatedHotelAddon.PartyReservationQuantity}
              onChange={(value) => onChange("PartyReservationQuantity", value)}
              disabled={isDerived}
            />
          </Form.Group>
          <hr style={{ margin: '20px 0' }} />
          <Form.Group
            widths='equal'
          >
            <div className="checkbox-wrapper"
              style={{
                width: '100%',
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingLeft: 5,
                paddingRight: 5
              }
              }>
              {text("ConsumeDayAfterReservationDate")}
              <CheckBoxToggle
                checked={updatedHotelAddon?.IsConsumptionAfterReservationDay ?? isConsumptionAfterReservationDayDefault}
                onChange={(value) => onChange("IsConsumptionAfterReservationDay", value)}
                disabled={isDerived}
              />
            </div>
          </Form.Group>
          <Form.Group
            widths='equal'
          >
            <div className="checkbox-wrapper"
              style={{
                width: '100%',
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingLeft: 5,
                paddingRight: 5
              }
              }>
              {text("DistributeToExternalChannel")}
              <CheckBoxToggle
                checked={updatedHotelAddon?.IsDistributeToChannelManager}
                onChange={(value) => onChange("IsDistributeToChannelManager", value)}
              />
            </div>
          </Form.Group>
          <hr style={{ margin: '20px 0' }} />
          <Form.Group
            widths='equal'
          >
            <Form.Field
              control={AddonsConsumptionTypesDropDown}
              label={text('PricePostingSettings')}
              selectFirst
              value={updatedHotelAddon.ConsumptionType}
              onChange={(val) => onChange("ConsumptionType", val)}
              disabled={isDerived}
            />
          </Form.Group>
          <Form.Group
            widths='equal'
            style={{ marginTop: 20 }}
          >
            {updatedHotelAddon.ConsumptionType ===
              AddonConsumptionType.EveryXNights && (
                <Form.Field
                  control={NumericInput}
                  selectOnFocus
                  digits={0}
                  buttonPlacement='right'
                  placeholder='ex: 2'
                  onChange={(value) =>
                    onChange('EveryXnights', parseInt(value))
                  }
                  min={minEveryXnightsValue}
                  value={updatedHotelAddon.EveryXnights || ''}
                  disabled={
                    isDerived ||
                    updatedHotelAddon.ConsumptionType !==
                    AddonConsumptionType.EveryXNights
                  }
                />
              )}
            {updatedHotelAddon.ConsumptionType ===
              AddonConsumptionType.OnSpecificWeekdays && (
                <Form.Field
                  disabled={
                    isDerived ||
                    updatedHotelAddon.ConsumptionType !==
                    AddonConsumptionType.OnSpecificWeekdays
                  }
                  control={WeekDaysSelect}
                  useWithoutDates
                  initDays={makeIntToDaysOfWeekArray(hotelAddon?.OnSpecificWeekdays)}
                  onChange={(value) =>
                    onChange('OnSpecificWeekdays', makeDaysOfWeekArrayIntoInt(value))
                  }
                />
              )}
            {updatedHotelAddon.ConsumptionType ===
              AddonConsumptionType.OnSpecificNightsOfTheStay && (
                <Form.Input
                  value={updatedHotelAddon?.OnSpecificNightsOfTheStay}
                  placeholder='ex: 2,4,7'
                  disabled={
                    isDerived ||
                    updatedHotelAddon.ConsumptionType !==
                    AddonConsumptionType.OnSpecificNightsOfTheStay
                  }
                  onChange={({ target }) =>
                    onChange('OnSpecificNightsOfTheStay', target.value)
                  }
                />
              )}
          </Form.Group>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button negative onClick={onClose}>
          {text("Close")}
        </Button>
        {!isDerived && <Button positive onClick={onSave}>
          {text("Save")}
        </Button>}
      </Modal.Actions>
    </Modal>
  );
};

AddonOptionsPopup.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  hotelAddon: PropTypes.object,
  onUpdate: PropTypes.func,
  isDerived: PropTypes.bool,
  rateElementName: PropTypes.string,
  isConsumptionAfterReservationDayDefault: PropTypes.bool
};
